/**
 * @file BillingServicesTab.js
 * @description Tab component for managing a client's billed services (feature, cost type/frequency, and amounts).
 */

import {
    useState,
    useEffect,
    useCallback,
    useContext,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react';
import { format, parseISO } from 'date-fns';
import Toast from '../../ui/toasts/Toast';
import Spinner from '../../ui/spinners/Spinner';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import styles from '../Billing.module.scss';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import { getServices, deleteService } from '../../../services/clientService';
import { getText } from '../../../i18n';
import {
    fetchBillFeatures,
    fetchBillCostTypes,
    fetchBillCostFrequencies,
} from '../../../services/systemService';

const formatDisplayDate = dateString => {
    if (!dateString) return '-';
    try {
        return format(parseISO(dateString), 'MMM yyyy');
    } catch (error) {
        return dateString;
    }
};

const getLookupLabel = (lookupList, id) =>
    lookupList.find(item => item.id === id)?.name || '-';

const BillingServicesTab = forwardRef(
    ({ clientId, onSelectionChange }, ref) => {
        const { showModal } = useContext(ModalContext);
        const [services, setServices] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const [selectedServiceIds, setSelectedServiceIds] = useState([]);
        const [features, setFeatures] = useState([]);
        const [costTypes, setCostTypes] = useState([]);
        const [costFrequencies, setCostFrequencies] = useState([]);
        const selectAllRef = useRef(null);

        const loadLookups = useCallback(async () => {
            try {
                const [featureData, costTypeData, costFrequencyData] =
                    await Promise.all([
                        fetchBillFeatures(),
                        fetchBillCostTypes(),
                        fetchBillCostFrequencies(),
                    ]);
                setFeatures(Array.isArray(featureData) ? featureData : []);
                setCostTypes(Array.isArray(costTypeData) ? costTypeData : []);
                setCostFrequencies(
                    Array.isArray(costFrequencyData) ? costFrequencyData : [],
                );
            } catch (error) {
                console.error('Failed to fetch service lookups', error);
            }
        }, []);

        const loadServices = useCallback(async () => {
            if (!clientId) return;

            try {
                setIsLoading(true);
                const response = await getServices(clientId);
                setServices(Array.isArray(response) ? response : []);
            } catch (error) {
                console.error('Failed to fetch services', error);
                Toast.error(getText('msg_2d50b4a8d20e'));
            } finally {
                setIsLoading(false);
            }
        }, [clientId]);

        useEffect(() => {
            loadLookups();
        }, [loadLookups]);

        useEffect(() => {
            loadServices();
        }, [loadServices]);

        useEffect(() => {
            if (onSelectionChange) {
                onSelectionChange(selectedServiceIds.length);
            }
        }, [selectedServiceIds, onSelectionChange]);

        useEffect(() => {
            if (selectAllRef.current) {
                selectAllRef.current.indeterminate =
                    selectedServiceIds.length > 0 &&
                    selectedServiceIds.length < services.length;
            }
        }, [selectedServiceIds, services]);

        const toggleServiceSelection = serviceId => {
            setSelectedServiceIds(prevSelected =>
                prevSelected.includes(serviceId)
                    ? prevSelected.filter(id => id !== serviceId)
                    : [...prevSelected, serviceId],
            );
        };

        const handleSelectAll = event => {
            setSelectedServiceIds(
                event.target.checked ? services.map(service => service.id) : [],
            );
        };

        const handleDeleteSelected = () => {
            showModal({
                type: ModalTypes.DELETE_CONFIRMATION,
                title: getText('msg_dbff3e6ef33d'),
                text: getText('msg_c7b1018e4f42'),
                yesButtonText: getText('msg_f6fdbe48dc54'),
                noButtonText: getText('msg_77dfd2135f4d'),
                onYes: async () => {
                    try {
                        await Promise.all(
                            selectedServiceIds.map(id =>
                                deleteService(clientId, id),
                            ),
                        );
                        Toast.success(getText('msg_6bd969b9cd1d'));
                        setSelectedServiceIds([]);
                        loadServices();
                    } catch (error) {
                        console.error('Failed to delete services', error);
                        Toast.error(getText('msg_b029750db80b'));
                    }
                },
                onNo: () => {},
            });
        };

        useImperativeHandle(ref, () => ({
            deleteSelected: handleDeleteSelected,
        }));

        const openAddModal = () => {
            showModal({
                type: ModalTypes.ADD_SERVICE,
                data: { clientId },
                onSuccess: loadServices,
            });
        };

        if (isLoading) {
            return <Spinner width={50} className={styles.loadingSpinner} />;
        }

        return (
            <div className="col-12">
                <div className="table-responsive w-100">
                    <table className="table table-hover align-middle">
                        <thead className="custom-table-header table-light">
                            <tr>
                                <th
                                    className="text-center"
                                    style={{ width: 40 }}
                                >
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        ref={selectAllRef}
                                        checked={
                                            selectedServiceIds.length ===
                                                services.length &&
                                            services.length > 0
                                        }
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th>{getText('msg_ad565d9d01d0')}</th>
                                <th>{getText('msg_64e3d203d167')}</th>
                                <th>{getText('msg_e9c417d410e1')}</th>
                                <th>{getText('msg_8b4360affdb0')}</th>
                                <th>{getText('msg_120d93985118')}</th>
                                <th>{getText('msg_84b147812589')}</th>
                                <th>{getText('msg_43dc8532f7e5')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {services.map(service => (
                                <tr key={service.id}>
                                    <td
                                        className="text-center"
                                        style={{ width: 40 }}
                                    >
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedServiceIds.includes(
                                                service.id,
                                            )}
                                            onChange={() =>
                                                toggleServiceSelection(
                                                    service.id,
                                                )
                                            }
                                        />
                                    </td>
                                    <td>
                                        {getLookupLabel(
                                            features,
                                            service.billFeatureId,
                                        )}
                                    </td>
                                    <td className="item-page-changed-by">
                                        {getLookupLabel(
                                            costTypes,
                                            service.billCostTypeId,
                                        )}
                                    </td>
                                    <td className="item-page-changed-by">
                                        {getLookupLabel(
                                            costFrequencies,
                                            service.billCostFrequencyId,
                                        )}
                                    </td>
                                    <td className="item-page-changed-by">
                                        $
                                        {Number(
                                            service.minimumCost || 0,
                                        ).toFixed(2)}
                                    </td>
                                    <td className="item-page-changed-by">
                                        {formatDisplayDate(
                                            service.effectiveDate,
                                        )}
                                    </td>
                                    <td className="item-page-changed-by">
                                        {formatDisplayDate(service.endDate)}
                                    </td>
                                    <td className="item-page-changed-by">
                                        $
                                        {Number(service.amount || 0).toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {services.length === 0 && (
                        <NotFoundBox title={getText('msg_f506de111214')} />
                    )}
                </div>

                <span
                    type="button"
                    onClick={openAddModal}
                    className="ing-text-buttons mt-3 d-inline-flex align-items-center ps-4"
                >
                    <i
                        className={`bi bi-plus-circle-fill me-2 ${styles.addIconBadge}`}
                    />
                    {getText('msg_45e0f536dd5d')}
                </span>
            </div>
        );
    },
);

export default BillingServicesTab;
